// components/offers&discountpage.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ref, onValue } from '../lib/firebase-database';
import { db } from '../firebase';
import { Heart } from 'lucide-react';
import { Layout } from './Layout';
import { updateVilla } from '../services/appwriteDB';
import OptimizedImage from "./OptimizedImage";

const parseDiscountValue = (value, fallback = 0) => {
  const parsed = Number.parseInt(value, 10);
  if (Number.isNaN(parsed)) return fallback;
  return Math.max(0, Math.min(100, parsed));
};

const OffersDiscountPage = ({ cachedProperties, updateCachedProperties }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Local state to hold temporary edits
  const [editedProperties, setEditedProperties] = useState({});

  useEffect(() => {
    if (cachedProperties && cachedProperties.length > 0) {
      setProperties(cachedProperties);
      setFilteredProperties(cachedProperties);
      setIsLoading(false);
    } else {
      fetchProperties();
    }
  }, [cachedProperties]);

  const checkAndRemoveExpiredOffers = (properties) => {
    const now = new Date().getTime();
    properties.forEach(property => {
      if (property.isFeaturedOffer && property.offerValidTill) {
        const validTill = new Date(property.offerValidTill).getTime();
        if (validTill < now) {
          const nextPricing = {
            ...(property.pricing || {}),
            isFeaturedOffer: false,
            discount: 0,
          };
          updateVilla(property._id, {
            pricing: nextPricing,
          }).catch((error) => {
            console.error('Failed to auto-remove expired offer:', error);
          });
        }
      }
    });
  };

  const fetchProperties = () => {
    setIsLoading(true);
    const villasRef = ref(db, 'villas');
    onValue(villasRef, (snapshot) => {
      const data = snapshot.val();
      const fetchedProperties = data
        ? Object.entries(data).map(([key, value]) => ({
            _id: key,
            ...value,
          }))
        : [];
      
      checkAndRemoveExpiredOffers(fetchedProperties);
      setProperties(fetchedProperties);
      setFilteredProperties(fetchedProperties);
      if (typeof updateCachedProperties === 'function') {
        updateCachedProperties(fetchedProperties);
      }
      setIsLoading(false);
    });
  };

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    const filtered = properties.filter((property) =>
      property.name.toLowerCase().includes(query) ||
      property.mainCity.toLowerCase().includes(query)
    );
    setFilteredProperties(filtered);
  };

  // Update local state for temporary edits
  const handleInputChange = (propertyId, field, value) => {
    setEditedProperties((prev) => ({
      ...prev,
      [propertyId]: {
        ...prev[propertyId],
        [field]: value,
      },
    }));
  };

  // Confirm and save changes to Firebase
  const handleConfirmOffer = (propertyId) => {
    const property = properties.find((p) => p._id === propertyId);
    const edited = editedProperties[propertyId] || {};

    if (!property) {
      alert('Property not found. Please refresh and try again.');
      return;
    }

    const resolvedDiscount = edited.discount !== undefined
      ? parseDiscountValue(edited.discount, 0)
      : parseDiscountValue(property.discount, 0);

    if (resolvedDiscount <= 0) {
      alert('Please add a discount before confirming this offer');
      return;
    }

    const offerMessage = (edited.offerMessage ?? property.offerMessage)?.trim();
    const termsAndConditions = (edited.termsAndConditions ?? property.termsAndConditions)?.trim();

    // Keep offerValidTill type aligned with existing data where possible.
    const existingValidTill = property.offerValidTill;
    let offerValidTill;
    if (edited.offerValidTill) {
      offerValidTill = typeof existingValidTill === 'number'
        ? new Date(`${edited.offerValidTill}T00:00:00`).getTime()
        : edited.offerValidTill;
    } else if (existingValidTill !== undefined && existingValidTill !== null) {
      offerValidTill = existingValidTill;
    }

    const updates = {
      pricing: {
        ...(property.pricing || {}),
        discount: resolvedDiscount,
        ...(offerValidTill !== undefined ? { offerValidTill } : {}),
        ...(offerMessage ? { offerMessage } : {}),
        ...(termsAndConditions ? { termsAndConditions } : {}),
        isFeaturedOffer: true,
      },
    };

    updateVilla(propertyId, updates)
      .then(() => {
        alert('Offer updated successfully!');
        fetchProperties();
      })
      .catch((error) => {
        console.error('Error updating offer:', error);
        alert(`Failed to update offer. ${error?.message || 'Please try again.'}`);
      });
  };

  // Remove offer from home page
  const handleRemoveFromHome = (propertyId) => {
    const property = properties.find((p) => p._id === propertyId);
    if (!property) {
      alert('Property not found. Please refresh and try again.');
      return;
    }

    const nextPricing = {
      ...(property.pricing || {}),
      isFeaturedOffer: false,
      discount: 0,
    };

    updateVilla(propertyId, {
      pricing: nextPricing,
    })
      .then(() => {
        alert('Offer removed from home page successfully!');
        fetchProperties(); // Refresh the list
      })
      .catch((error) => {
        console.error('Error removing offer:', error);
        alert('Failed to remove offer. Please try again.');
      });
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Offers & Discounts
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Set discounts and feature properties on the homepage.
          </p>
        </div>

        <div className="relative max-w-md">
          <input
            type="text"
            placeholder="Search by name or city"
            value={searchQuery}
            onChange={handleSearch}
            className="w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-black"
          />
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-gray-500">
            <div className="h-5 w-5 border-2 border-gray-300 border-t-black rounded-full animate-spin mr-2" />
            Loading villas...
          </div>
        ) : filteredProperties.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg py-16 text-center text-sm text-gray-500">
            No villas to show.
          </div>
        ) : (
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
            {filteredProperties.map((property) => {
              const edited = editedProperties[property._id] || {};
              const currentDiscount =
                edited.discount !== undefined
                  ? parseInt(edited.discount, 10) || 0
                  : property.discount || 0;
              const discountedPrice = Math.round(
                property.rate * (1 - currentDiscount / 100)
              );

              return (
                <div
                  key={property._id}
                  className="bg-white border border-gray-200 rounded-lg overflow-hidden flex flex-col"
                >
                  <div className="relative w-full h-44 bg-gray-100">
                    <OptimizedImage
                      src={
                        property.thumbnail ||
                        property.images?.[0] ||
                        "/placeholder.svg"
                      }
                      alt={property.name}
                      className="w-full h-full object-cover"
                      preset="cardSmall"
                    />
                    {property.isFeaturedOffer && (
                      <span className="absolute top-2 left-2 text-[11px] font-medium px-1.5 py-0.5 rounded bg-black text-white">
                        Featured
                      </span>
                    )}
                  </div>

                  <div className="p-4 space-y-3 flex-1 flex flex-col">
                    <div>
                      <h2 className="text-sm font-semibold text-gray-900 line-clamp-1">
                        {property.name}
                      </h2>
                      <p className="text-xs text-gray-500 line-clamp-1">
                        {property.mainCity}
                      </p>
                    </div>

                    <div className="grid grid-cols-1 gap-2.5">
                      <label className="block">
                        <span className="text-xs font-medium text-gray-700">
                          Discount (%)
                        </span>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={
                            edited.discount !== undefined
                              ? edited.discount
                              : property.discount || 0
                          }
                          onChange={(e) =>
                            handleInputChange(
                              property._id,
                              "discount",
                              e.target.value
                            )
                          }
                          className="mt-1 w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-black"
                        />
                      </label>

                      <label className="block">
                        <span className="text-xs font-medium text-gray-700">
                          Offer message
                        </span>
                        <textarea
                          rows="2"
                          value={
                            edited.offerMessage ?? property.offerMessage ?? ""
                          }
                          onChange={(e) =>
                            handleInputChange(
                              property._id,
                              "offerMessage",
                              e.target.value
                            )
                          }
                          placeholder="Limited weekend offer"
                          className="mt-1 w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-black resize-none"
                        />
                      </label>

                      <label className="block">
                        <span className="text-xs font-medium text-gray-700">
                          Valid till
                        </span>
                        <input
                          type="date"
                          value={
                            edited.offerValidTill
                              ? new Date(edited.offerValidTill)
                                  .toISOString()
                                  .split("T")[0]
                              : property.offerValidTill
                                ? new Date(property.offerValidTill)
                                    .toISOString()
                                    .split("T")[0]
                                : ""
                          }
                          onChange={(e) =>
                            handleInputChange(
                              property._id,
                              "offerValidTill",
                              e.target.value
                            )
                          }
                          className="mt-1 w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-black"
                        />
                      </label>

                      <label className="block">
                        <span className="text-xs font-medium text-gray-700">
                          Terms & conditions
                        </span>
                        <textarea
                          rows="2"
                          value={
                            edited.termsAndConditions ??
                            property.termsAndConditions ??
                            ""
                          }
                          onChange={(e) =>
                            handleInputChange(
                              property._id,
                              "termsAndConditions",
                              e.target.value
                            )
                          }
                          placeholder="Cancellation, deposits, etc."
                          className="mt-1 w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-black resize-none"
                        />
                      </label>
                    </div>

                    <div className="text-xs text-gray-500 pt-1 border-t border-gray-100">
                      <div>Rate: ₹{property.rate}/night</div>
                      {currentDiscount > 0 && (
                        <div className="font-medium text-gray-900">
                          After discount: ₹{discountedPrice}/night
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-3 gap-2 mt-auto">
                      <button
                        type="button"
                        onClick={() => handleConfirmOffer(property._id)}
                        className="col-span-2 inline-flex items-center justify-center px-3 py-2 text-xs font-medium text-white bg-black rounded-md hover:bg-gray-900"
                      >
                        {property.isFeaturedOffer ? "Update" : "Confirm"}
                      </button>
                      <button
                        type="button"
                        onClick={() => navigate(`/villa/${property._id}`)}
                        className="inline-flex items-center justify-center px-3 py-2 text-xs font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                      >
                        View
                      </button>
                      {property.isFeaturedOffer && (
                        <button
                          type="button"
                          onClick={() => handleRemoveFromHome(property._id)}
                          className="col-span-3 inline-flex items-center justify-center px-3 py-2 text-xs font-medium text-red-600 border border-red-200 rounded-md hover:bg-red-50"
                        >
                          Remove from homepage
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

OffersDiscountPage.defaultProps = {
  cachedProperties: [],
  updateCachedProperties: () => {}, // Provide default no-op function
};

export default OffersDiscountPage;